import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { ACHIEVEMENTS, type AchievementDef } from '../pages/AchievementPage';
import { playNotify, playCoin } from '../utils/sounds';

interface NotificationItem {
  achievement: AchievementDef;
  id: string;
}

const NOTIFIED_STORAGE_KEY = 'vision-quest-notified-achievements';

function getNotifiedIds(): Set<string> {
  try {
    const stored = localStorage.getItem(NOTIFIED_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch { /* ignore */ }
  return new Set();
}

function saveNotifiedIds(ids: Set<string>) {
  localStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify([...ids]));
}

export default function AchievementNotification() {
  const navigate = useNavigate();
  const { levelResults, consecutiveDays, totalEnergy, eyeProtectionTotalSeconds, visionQuestionnaireResults, visionRecords } = useGameStore();
  
  const [queue, setQueue] = useState<NotificationItem[]>([]);
  const [current, setCurrent] = useState<NotificationItem | null>(null);
  const notifiedRef = useRef<Set<string>>(getNotifiedIds());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compute currently unlocked achievements
  const computeUnlocked = useCallback((): string[] => {
    const unlocked: string[] = [];
    
    const categoriesPlayed = new Set(levelResults.filter(r => r.levelId).map(r => {
      const parts = r.levelId.split('-');
      return parts[0];
    }));

    for (const ach of ACHIEVEMENTS) {
      let isUnlocked = false;

      switch (ach.category) {
        case 'training':
          isUnlocked = levelResults.length >= ach.target;
          break;
        case 'streak':
          isUnlocked = consecutiveDays >= ach.target;
          break;
        case 'performance':
          if (ach.id.startsWith('acc_')) {
            const bestAccuracy = levelResults.length > 0 
              ? Math.max(...levelResults.map(r => r.accuracy)) 
              : 0;
            isUnlocked = bestAccuracy >= ach.target;
          } else if (ach.id.startsWith('react_')) {
            const bestReaction = levelResults.some(r => r.reactionTime > 0)
              ? Math.min(...levelResults.filter(r => r.reactionTime > 0).map(r => r.reactionTime))
              : 9999;
            isUnlocked = bestReaction <= ach.target && levelResults.length > 0;
          }
          break;
        case 'milestone':
          if (ach.id.startsWith('energy_')) {
            isUnlocked = totalEnergy >= ach.target;
          } else if (ach.id === 'games_3cat' || ach.id === 'games_6cat') {
            isUnlocked = categoriesPlayed.size >= ach.target;
          }
          break;
        case 'special':
          if (ach.id === 'first_perfect') {
            isUnlocked = levelResults.some(r => r.accuracy >= 100);
          } else if (ach.id === 'night_owl' || ach.id === 'eye_guard') {
            isUnlocked = eyeProtectionTotalSeconds >= ach.target;
          } else if (ach.id === 'questionnaire') {
            isUnlocked = visionQuestionnaireResults.length >= ach.target;
          } else if (ach.id === 'vision_record') {
            isUnlocked = visionRecords.length >= ach.target;
          }
          break;
      }

      if (isUnlocked) unlocked.push(ach.id);
    }

    return unlocked;
  }, [levelResults, consecutiveDays, totalEnergy, eyeProtectionTotalSeconds, visionQuestionnaireResults, visionRecords]);

  // Check for new unlocks
  useEffect(() => {
    const currentUnlocked = computeUnlocked();
    const newUnlocks: NotificationItem[] = [];

    for (const id of currentUnlocked) {
      if (!notifiedRef.current.has(id)) {
        const ach = ACHIEVEMENTS.find(a => a.id === id);
        if (ach) {
          newUnlocks.push({ achievement: ach, id });
          notifiedRef.current.add(id);
        }
      }
    }

    if (newUnlocks.length > 0) {
      saveNotifiedIds(notifiedRef.current);
      setQueue(prev => [...prev, ...newUnlocks]);
    }
  }, [computeUnlocked]);

  // Show next notification from queue
  useEffect(() => {
    if (!current && queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrent(next);
      setQueue(rest);
      playNotify();
      setTimeout(() => playCoin(), 300);
      
      timerRef.current = setTimeout(() => {
        setCurrent(null);
      }, 4500);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, queue]);

  const handleDismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrent(null);
  };

  const handleNavigate = () => {
    handleDismiss();
    navigate('/achievements');
  };

  const tierLabel = (tier: string) => tier === 'bronze' ? '铜' : tier === 'silver' ? '银' : '金';
  const tierGradient = (tier: string) => 
    tier === 'gold' ? 'from-yellow-400 to-amber-500' :
    tier === 'silver' ? 'from-gray-300 to-gray-400' :
    'from-amber-600 to-orange-700';

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ y: -100, opacity: 0, scale: 0.8 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -60, opacity: 0, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[9998] w-[calc(100%-2rem)] max-w-sm"
        >
          <div 
            onClick={handleNavigate}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl shadow-amber-200/40 dark:shadow-black/40 border-2 border-amber-300 dark:border-amber-600 p-4 cursor-pointer active:scale-[0.98] transition-transform"
          >
            {/* Shimmer overlay */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-100/30 to-transparent dark:via-amber-500/10"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              />
            </div>

            <div className="flex items-center gap-3 relative">
              {/* Icon with tier ring */}
              <div className="relative">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tierGradient(current.achievement.tier)} flex items-center justify-center shadow-md`}>
                  <span className="text-2xl">{current.achievement.icon}</span>
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring' }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center shadow-sm"
                >
                  <span className="text-[8px] font-bold text-white">{tierLabel(current.achievement.tier)}</span>
                </motion.div>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <motion.span
                    animate={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-sm"
                  >
                    🎉
                  </motion.span>
                  <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">成就解锁！</span>
                </div>
                <h3 className="font-bold text-sm text-gray-800 dark:text-gray-100 truncate mt-0.5">
                  {current.achievement.name}
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {current.achievement.description}
                </p>
              </div>

              {/* Reward badge */}
              <div className="flex-shrink-0">
                <motion.div
                  initial={{ scale: 0, rotate: -90 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                  className="bg-gradient-to-br from-amber-400 to-orange-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-lg shadow-md"
                >
                  +{current.achievement.reward}⚡
                </motion.div>
              </div>
            </div>

            {/* Tap to view hint */}
            <p className="text-[9px] text-gray-400 dark:text-gray-500 text-center mt-2">
              点击查看详情 · 自动关闭
            </p>

            {/* Progress bar for auto-dismiss */}
            <motion.div
              className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 4.5, ease: 'linear' }}
            />
          </div>

          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            ✕
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
